/**
 * Unit economics for a seller's own site paid through Ozon Pay.
 *
 * The buyer pays delivery, therefore seller delivery and marketplace
 * logistics are deliberately zero in this model. The seller still pays
 * acquiring, optional Ozon Pay online checks, taxes and own overhead.
 * Tariffs checked on 21.09.2026:
 * - bank cards: 2.2%;
 * - SBP: 0.4–0.7% depending on the category (entered manually);
 * - optional Ozon Pay online checks: 0.49% of turnover.
 */

export const SITE_OZON_PAY_2026_RULESET = {
  id: "site-ozon-pay-2026-v1",
  label: "Свой сайт · Ozon Pay · версия 1",
  source: "Тарифы интернет-эквайринга Ozon Банка, проверены 21.09.2026",
};

export const SITE_OZON_PAY_TAX_MODES = [
  { value: "usn6", label: "УСН Доходы 6% · без НДС" },
  { value: "usn6_vat5", label: "УСН Доходы 6% · НДС 5%" },
  { value: "usn6_vat7", label: "УСН Доходы 6% · НДС 7%" },
  { value: "ausn8", label: "АУСН Доходы 8% · без НДС" },
  { value: "ausn8_vat5", label: "АУСН Доходы 8% · НДС 5%" },
  { value: "ausn8_vat7", label: "АУСН Доходы 8% · НДС 7%" },
];

export const SITE_OZON_PAY_2026_DEFAULT_INPUTS = {
  price: 0,
  overhead: 0,
  // Default is payment by card. For SBP enter the contracted 0.4–0.7% rate.
  acquiringPercent: 2.2,
  // Enabled by default for the site calculation. Set to 0 only when the
  // service is not connected or checks are paid through another provider.
  onlineChecksPercent: 0.49,
  advertisingPercent: 0,
  taxMode: "usn6",
};

const numberOr = (value, fallback = 0) => {
  const parsed =
    typeof value === "string" ? Number(value.replace(",", ".")) : Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};
const nonNegative = (value, fallback = 0) =>
  Math.max(0, numberOr(value, fallback));
const percent = (value, fallback = 0) =>
  Math.min(100, nonNegative(value, fallback));
const divide = (value, divisor) => (divisor ? value / divisor : 0);

export function normalizeSiteOzonPay2026Inputs(value = {}) {
  const defaults = SITE_OZON_PAY_2026_DEFAULT_INPUTS;
  return {
    price: nonNegative(value.price, defaults.price),
    overhead: nonNegative(value.overhead, defaults.overhead),
    acquiringPercent: percent(
      value.acquiringPercent,
      defaults.acquiringPercent,
    ),
    onlineChecksPercent: percent(
      value.onlineChecksPercent,
      defaults.onlineChecksPercent,
    ),
    advertisingPercent: percent(
      value.advertisingPercent,
      defaults.advertisingPercent,
    ),
    taxMode: SITE_OZON_PAY_TAX_MODES.some(
      (mode) => mode.value === value.taxMode,
    )
      ? value.taxMode
      : defaults.taxMode,
  };
}

function taxRates(mode) {
  return {
    income: mode.startsWith("ausn8") ? 0.08 : 0.06,
    vat: mode.endsWith("vat5") ? 0.05 : mode.endsWith("vat7") ? 0.07 : 0,
  };
}

export function calculateSiteOzonPay2026(rawInputs = {}, rawCostPrice = 0) {
  const inputs = normalizeSiteOzonPay2026Inputs(rawInputs);
  const costPrice = nonNegative(rawCostPrice);
  // The editable product cost already includes packaging, consistent with the
  // other calculators. Only seller-specific overhead is added here.
  const totalCost = costPrice + inputs.overhead;
  const acquiring = inputs.price * (inputs.acquiringPercent / 100);
  const onlineChecks = inputs.price * (inputs.onlineChecksPercent / 100);
  const { income: incomeTaxRate, vat: vatRate } = taxRates(inputs.taxMode);
  const vat = inputs.price * vatRate;
  const incomeTax = (inputs.price - vat) * incomeTaxRate;
  const tax = vat + incomeTax;
  const advertisingSpend = inputs.price * (inputs.advertisingPercent / 100);
  const marketplaceExpenses = acquiring + onlineChecks + tax;
  const payoutBeforeTax = inputs.price - acquiring - onlineChecks;
  const profit = payoutBeforeTax - tax - totalCost;
  const profitAfterAdvertising = profit - advertisingSpend;

  return {
    rulesetId: SITE_OZON_PAY_2026_RULESET.id,
    totalCost,
    // These compatibility fields let the common comparison table display the
    // site's payment expenses while explicitly showing no seller logistics.
    volumeLiters: 0,
    commission: 0,
    commissionRate: 0,
    logistics: 0,
    acquiring,
    onlineChecks,
    vat,
    incomeTax,
    tax,
    marketplaceExpenses,
    payoutBeforeTax,
    netRevenue: payoutBeforeTax - tax,
    profit,
    margin: divide(profit, inputs.price),
    advertisingSpend,
    profitAfterAdvertising,
    marginAfterAdvertising: divide(profitAfterAdvertising, inputs.price),
    profitability: divide(profitAfterAdvertising, totalCost),
  };
}
